import React, { useCallback, useEffect, useState } from 'react';

import { createStyles } from '@mantine/core';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscCellClickEvent,
  MbscEventClickEvent,
} from '@mobiscroll/react';
import { IDatetimeProps } from '@mobiscroll/react/dist/src/core/util/datetime';
import dayjs from 'dayjs';
import useAppointmentWeek from 'hooks/appointment/useAppointmentWeek';
import useClassWeek from 'hooks/class/useClassWeek';
import useStaffAvailabilityCalendar from 'hooks/staffAvailability/useStaffAvailabilityCalendar';
import useDebounce from 'hooks/useDebounce';
import { AppointmentWeek, KiloClassWeek } from 'types';
import { isDateTimePast } from 'utils/date-functions';
import { getAppointmentEventTitle } from 'utils/event.helpers';

import ModalDragNotificationConfirmation from './ModalDragNotificationConfirmation';
import ModalDragRecurrenceConfirmation from './ModalDragRecurrenceConfirmation';
import useEventDragUpdate, { DragPrefill } from '../hooks/useEventDragUpdate';

const isClassDraggable = (event: KiloClassWeek): boolean => {
  if (isDateTimePast(event.startDate, event.startTime)) return false;
  return !event.athleteKiloClasses.some(akc => akc.isCheckedIn || akc.isLateCancel || akc.isNoShow);
};

const isAppointmentDraggable = (event: AppointmentWeek): boolean => {
  if (isDateTimePast(event.startDate, event.startTime)) return false;
  return !event.athleteAppointments.some(aa => aa.isCheckedIn || aa.isLateCancel || aa.isNoShow);
};

const useStyles = createStyles(theme => ({
  calendar: {
    fontFamily: `${theme.fontFamily} !important`,
    borderRadius: theme.radius.md,
    '& .mbsc-schedule-event-title': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .mbsc-schedule-event-resize-start': {
      display: 'none !important',
    },
  },
}));

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface IEventCalendarWeekViewProps {
  eventCalendarNavigationButtons: () => JSX.Element;
  handleOpenSelectEventModal: () => void;
  searchFilters: FilterTerms;
  calendarMaxDate?: Date;
  handleEditClassEvent: (kcId: string, prefill?: DragPrefill) => void;
  handleEditAppointmentEvent: (kcId: string, prefill?: DragPrefill) => void;
  setCreateEventDate: React.Dispatch<Date>;
  permission?: boolean;
  isDesktop: boolean;
}

const EventCalendarWeekView = ({
  eventCalendarNavigationButtons,
  handleOpenSelectEventModal,
  searchFilters,
  calendarMaxDate,
  handleEditClassEvent,
  handleEditAppointmentEvent,
  setCreateEventDate,
  permission = true,
  isDesktop,
}: IEventCalendarWeekViewProps): JSX.Element => {
  const { classes } = useStyles();
  const { handleEventUpdate, handleEventUpdated, recurrenceModal, notificationModal } = useEventDragUpdate({
    handleEditClassEvent,
    handleEditAppointmentEvent,
  });
  const [start, setStart] = useState<string>();
  const [end, setEnd] = useState<string>();

  const debouncedCoaches = useDebounce(searchFilters.coaches, 200);
  const debouncedStart = useDebounce(start, 200);
  const debouncedEnd = useDebounce(end, 200);

  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);

  const hasValidStartDateAndEndDate = debouncedStart && debouncedEnd;
  const fullStartDate = hasValidStartDateAndEndDate
    ? new Date(`${dayjs(debouncedStart).format('YYYY-MM-DD')}T00:00`)
    : null;
  const fullEndDate = hasValidStartDateAndEndDate
    ? new Date(`${dayjs(debouncedEnd).format('YYYY-MM-DD')}T00:00`)
    : null;

  const formattedStartDate = fullStartDate ? dayjs(fullStartDate).toISOString() : '';
  const formattedEndDate = fullEndDate ? dayjs(fullEndDate).add(1, 'days').toISOString() : null;

  const { data: staffAvailabilityInvalidDatesData } = useStaffAvailabilityCalendar({
    coaches: debouncedCoaches,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    enabled: !!debouncedCoaches?.filter((c: string) => c !== 'None').length,
  });

  useEffect(() => {
    const today = dayjs();
    const s = dayjs(start);
    const e = dayjs(end);
    const isCurrentWeek = s.isBefore(today) && e.isAfter(today);
    setCreateEventDate(isCurrentWeek ? today.toDate() : s.toDate());
  }, [start, end, setCreateEventDate]);

  const handleCellClick = useCallback(
    (event: MbscCellClickEvent): void => {
      if (permission) {
        setCreateEventDate(event.date);
        handleOpenSelectEventModal();
      }
    },
    [handleOpenSelectEventModal, permission, setCreateEventDate],
  );

  const {
    data: classData,
    isError: classIsError,
    isSuccess: classIsSuccess,
  } = useClassWeek({
    start: debouncedStart,
    end: debouncedEnd,
    ...searchFilters,
  });

  const {
    data: appointmentData,
    isError: appointmentIsError,
    isSuccess: appointmentIsSuccess,
  } = useAppointmentWeek({
    start: debouncedStart,
    end: debouncedEnd,
    ...searchFilters,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
    lastDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const s = dayjs(event.firstDay).format('YYYY-MM-DD');
    const e = dayjs(event.lastDay).format('YYYY-MM-DD');
    requestAnimationFrame(() => {
      setStart(s);
      setEnd(e);
    });
  };

  const handleEventClick = useCallback(
    (e: MbscEventClickEvent): void => {
      if (e.event.eventType === 'class') {
        handleEditClassEvent(e.event.id?.toString() || '');
      }
      if (e.event.eventType === 'appointment') {
        handleEditAppointmentEvent(e.event.id?.toString() || '');
      }
    },
    [handleEditClassEvent, handleEditAppointmentEvent],
  );

  useEffect(() => {
    if (appointmentIsSuccess && classIsSuccess && appointmentData && classData) {
      const events = [
        ...classData.data.map((event: KiloClassWeek): MbscCalendarEvent => {
          const startTime = new Date(`${event.startDate}T${event.startTime}`);
          const endTime = new Date(`${event.startDate}T${event.startTime}`);
          endTime.setMinutes(startTime.getMinutes() + event.duration);
          return {
            ...event,
            eventType: 'class',
            id: event.kiloClassId,
            start: startTime,
            end: endTime,
            allDay: false,
            nonDraggable: !isClassDraggable(event),
            title: `${event.kiloClassType.name} ${
              event.athleteKiloClasses.filter(
                akc => (akc.isReserved || akc.isCheckedIn) && !akc.isLateCancel && !akc.isNoShow,
              ).length
            }/${event.capacity}`,
            color: event.kiloClassType.color,
          };
        }),
        ...appointmentData.data.map((event: AppointmentWeek): MbscCalendarEvent => {
          const startTime = new Date(`${event.startDate}T${event.startTime}`);
          const endTime = new Date(`${event.startDate}T${event.startTime}`);
          endTime.setMinutes(startTime.getMinutes() + event.duration);
          return {
            ...event,
            eventType: 'appointment',
            id: event.appointmentId,
            start: startTime,
            end: endTime,
            allDay: false,
            nonDraggable: !isAppointmentDraggable(event),
            title: getAppointmentEventTitle(event),
            color:
              event.athleteAppointments.length === 1 &&
              (event.athleteAppointments[0].isLateCancel || event.athleteAppointments[0].isNoShow)
                ? '#808080'
                : event.appointmentType.color,
          };
        }),
      ];
      setCalendarEvents(events);
    }
  }, [appointmentIsSuccess, classIsSuccess, appointmentData, classData]);

  if (appointmentIsError || classIsError) {
    return <>Something went wrong</>;
  }

  const invalidDates: IDatetimeProps['invalid'] = staffAvailabilityInvalidDatesData?.data?.map(i => ({
    ...i,
    cssClass: 'md-lunch-break-class mbsc-flex',
  }));

  return (
    <>
      <Eventcalendar
        renderHeader={eventCalendarNavigationButtons}
        theme='ios'
        themeVariant='light'
        className={classes.calendar}
        data={calendarEvents}
        view={{
          schedule: { type: 'week', allDay: false },
        }}
        dragToMove={permission}
        dragToResize={permission}
        dragTimeStep={15}
        onEventUpdate={handleEventUpdate}
        onEventUpdated={handleEventUpdated}
        onPageLoading={onPageLoading}
        onCellDoubleClick={handleCellClick}
        max={calendarMaxDate}
        onEventClick={handleEventClick}
        width={isDesktop ? '100%' : '100vw'}
        invalid={invalidDates}
      />
      <ModalDragRecurrenceConfirmation {...recurrenceModal} />
      <ModalDragNotificationConfirmation {...notificationModal} />
    </>
  );
};

export default EventCalendarWeekView;
